import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { incidents } from '../../utils/api';
import { toast } from 'sonner';
import { 
  AlertTriangle, CheckCircle2, Clock, RefreshCw, Search, 
  FileText, Store, User, X, Eye, XCircle
} from 'lucide-react';
import { HelpTooltip } from '../shared/HelpTooltip';

// Incident type labels
const INCIDENT_TYPES = {
  no_drlp_acknowledgment: { label: 'No Retailer Acknowledgment', color: 'bg-red-500' },
  no_ready_confirmation: { label: 'No Ready Confirmation', color: 'bg-red-500' },
  late_ready_confirmation: { label: 'Late Ready', color: 'bg-yellow-500' },
  payment_failed: { label: 'Payment Failed', color: 'bg-red-600' },
  delivery_failed: { label: 'Delivery Failed', color: 'bg-orange-500' },
  other: { label: 'Other', color: 'bg-gray-500' },
};

// Severity colors
const SEVERITY_COLORS = {
  critical: 'bg-red-600',
  high: 'bg-red-500',
  medium: 'bg-yellow-500',
  low: 'bg-blue-500',
};

export default function AdminIncidents({ user, onLogout }) {
  const [incidentList, setIncidentList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterResolved, setFilterResolved] = useState('unresolved');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, [filterType, filterSeverity, filterResolved]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Build filters
      const filters = {};
      if (filterType !== 'all') filters.incident_type = filterType;
      if (filterSeverity !== 'all') filters.severity = filterSeverity;
      if (filterResolved === 'resolved') filters.resolved = true;
      if (filterResolved === 'unresolved') filters.resolved = false;
      
      const [incidentsRes, statsRes] = await Promise.all([
        incidents.list(filters),
        incidents.stats(7)
      ]);
      
      setIncidentList(incidentsRes.data.incidents || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const openDetails = async (incident) => {
    try {
      const response = await incidents.get(incident.incident_id);
      setSelectedIncident(response.data);
      setAdminNotes(response.data.incident?.admin_notes || '');
      setDetailsOpen(true);
    } catch (error) {
      toast.error('Failed to load incident details');
    }
  };

  const handleDismiss = async (incidentId) => {
    setUpdating(true);
    try {
      await incidents.dismiss(incidentId);
      toast.success('Incident dismissed');
      setDetailsOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to dismiss incident');
    } finally {
      setUpdating(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedIncident) return;
    setUpdating(true);
    try {
      await incidents.update(selectedIncident.incident.incident_id, {
        admin_notes: adminNotes,
        resolved: true
      });
      toast.success('Incident resolved');
      setDetailsOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to resolve incident');
    } finally {
      setUpdating(false);
    }
  };

  // Filter incidents by search query
  const filteredIncidents = incidentList.filter(inc => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      inc.incident_id?.toLowerCase().includes(q) ||
      inc.order_id?.toLowerCase().includes(q) ||
      inc.drlp_name?.toLowerCase().includes(q) ||
      inc.dac_name?.toLowerCase().includes(q)
    );
  });

  const getTypeBadge = (type) => {
    const config = INCIDENT_TYPES[type] || INCIDENT_TYPES.other;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getSeverityBadge = (severity) => {
    const color = SEVERITY_COLORS[severity] || 'bg-gray-500';
    return <Badge className={color}>{severity?.toUpperCase()}</Badge>;
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Incident Reports
              </h1>
              <HelpTooltip portal="admin" category="incidents" tooltipKey="main" size="md" />
            </div>
            <p className="text-gray-600 mt-1">Monitor and resolve order incidents</p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total (7 days)</p>
                    <p className="text-2xl font-bold">{stats.total_incidents}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-gray-300" />
                </div>
              </CardContent>
            </Card>
            <Card className={stats.unreviewed > 0 ? 'border-red-200 bg-red-50' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Unreviewed</p>
                    <p className="text-2xl font-bold text-red-600">{stats.unreviewed}</p>
                  </div>
                  <Eye className="w-8 h-8 text-red-300" />
                </div>
              </CardContent>
            </Card>
            <Card className={stats.unresolved > 0 ? 'border-orange-200 bg-orange-50' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Unresolved</p>
                    <p className="text-2xl font-bold text-orange-600">{stats.unresolved}</p>
                  </div>
                  <Clock className="w-8 h-8 text-orange-300" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Top Offender</p>
                    <p className="text-lg font-bold truncate">
                      {stats.top_drlps?.[0]?.name || 'None'}
                    </p>
                  </div>
                  <Store className="w-8 h-8 text-gray-300" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by ID, retailer, or customer..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Incident Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(INCIDENT_TYPES).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterResolved} onValueChange={setFilterResolved}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="unresolved">Unresolved</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Incidents List */}
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Loading incidents...</p>
            </CardContent>
          </Card>
        ) : filteredIncidents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No incidents found</p>
              <p className="text-gray-400 text-sm">All systems operating normally</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredIncidents.map((incident) => (
              <Card 
                key={incident.incident_id} 
                className={`hover:shadow-md transition-shadow cursor-pointer ${
                  !incident.admin_reviewed ? 'border-l-4 border-l-red-500' : ''
                }`}
                onClick={() => openDetails(incident)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getSeverityBadge(incident.severity)}
                        {getTypeBadge(incident.type)}
                        {incident.resolved_at && (
                          <Badge variant="outline" className="bg-green-50 text-green-700">Resolved</Badge>
                        )}
                        {!incident.admin_reviewed && (
                          <Badge variant="outline" className="bg-red-50 text-red-700">New</Badge>
                        )}
                      </div>
                      <p className="font-semibold text-gray-900">{incident.incident_id}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Store className="w-4 h-4" />
                          {incident.drlp_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {incident.dac_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          Order #{incident.order_id?.slice(0, 8)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <p>{new Date(incident.created_at).toLocaleDateString()}</p>
                      <p>{new Date(incident.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Incident Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Incident Details
            </DialogTitle>
            <DialogDescription>
              {selectedIncident?.incident?.incident_id}
            </DialogDescription>
          </DialogHeader>

          {selectedIncident && (
            <div className="space-y-4">
              {/* Badges */}
              <div className="flex gap-2">
                {getSeverityBadge(selectedIncident.incident?.severity)}
                {getTypeBadge(selectedIncident.incident?.type)}
              </div>

              {/* Details */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-gray-500">Retailer</p>
                    <p className="font-medium">{selectedIncident.incident?.drlp_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Customer</p>
                    <p className="font-medium">{selectedIncident.incident?.dac_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Order ID</p>
                    <p className="font-medium">{selectedIncident.incident?.order_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Outcome</p>
                    <p className="font-medium">{selectedIncident.incident?.outcome}</p>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <p className="text-gray-500 mb-1">Timeline</p>
                  <div className="space-y-1 text-xs">
                    <p>Order Placed: {new Date(selectedIncident.incident?.order_placed_at).toLocaleString()}</p>
                    {selectedIncident.incident?.requested_ready_by && (
                      <p>Requested Ready By: {new Date(selectedIncident.incident?.requested_ready_by).toLocaleString()}</p>
                    )}
                    {selectedIncident.incident?.cancelled_at && (
                      <p className="text-red-600">Cancelled At: {new Date(selectedIncident.incident?.cancelled_at).toLocaleString()}</p>
                    )}
                  </div>
                </div>

                {selectedIncident.incident?.additional_data && Object.keys(selectedIncident.incident.additional_data).length > 0 && (
                  <div className="border-t pt-3">
                    <p className="text-gray-500 mb-1">Additional Info</p>
                    <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
                      {JSON.stringify(selectedIncident.incident.additional_data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* Admin Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700">Admin Notes</label>
                <Textarea
                  placeholder="Add notes about this incident..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => handleDismiss(selectedIncident?.incident?.incident_id)}
              disabled={updating}
            >
              <X className="w-4 h-4 mr-2" />
              Dismiss
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleResolve}
              disabled={updating}
            >
              {updating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Mark Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
